import { useState, useEffect, useRef } from 'react'

const BROKER_PHONES = {
  'Ben Rogers': '07 3666 8522',
  'Jesse Roach': '07 2150 1260',
  'Mitch Speer': '07 2150 1297',
}

const LEGACY_TEMPLATE_MAP = {
  'Quote Sent': 'quote_ready',
  'No Contact 1': 'missed_call',
  'No Contact 2': 'follow_up',
  'No Contact 3': 'final_follow_up',
  'Policy Placed': 'policy_confirmed',
  'Awaiting Client': 'awaiting_info',
}

const QUICK_STARTS = [
  { key: 'missed_call',      label: '📵 Missed you',      text: "Hi {{first}}, I tried to reach you — sorry I missed you! Give me a call back when you get a chance, or reply here.\n\n{{sig}}" },
  { key: 'quote_ready',      label: '📋 Quote ready',     text: "Hi {{first}}, your quote is ready and I've sent it through to your email. Have a look and let me know if you'd like to go ahead or have any questions.\n\n{{sig}}" },
  { key: 'follow_up',        label: '👋 Following up',    text: "Hi {{first}}, just following up — still keen to help get your insurance sorted. Happy to chat whenever suits.\n\n{{sig}}" },
  { key: 'policy_confirmed', label: '✅ Policy confirmed', text: "Hi {{first}}, your insurance is now confirmed and active! 🎉 Policy documents are on their way to your email. Any questions, just reach out.\n\n{{sig}}" },
  { key: 'renewal',          label: '🔄 Renewal',         text: "Hi {{first}}, your insurance renewal is coming up. I'll be in touch shortly with the best options — let me know if anything has changed in the meantime.\n\n{{sig}}" },
  { key: 'awaiting_info',    label: '⏳ Awaiting info',   text: "Hi {{first}}, just checking in — I'm still waiting on a couple of things before I can progress your insurance. Let me know if you need a hand.\n\n{{sig}}" },
  { key: 'final_follow_up',  label: '🏁 Last follow up',  text: "Hi {{first}}, this will be my last message for now — I don't want to be a pest! Your file is open whenever you're ready. Just reach out any time.\n\n{{sig}}" },
  { key: 'claim_update',     label: '🏥 Claim update',    text: "Hi {{first}}, quick update on your claim — things are progressing. I'll give you a call shortly to go through the details.\n\n{{sig}}" },
]

const SMS_TEMPLATES = {}
QUICK_STARTS.forEach(t => { SMS_TEMPLATES[t.key] = t })
export { SMS_TEMPLATES }

const fillText = (text, clientName, brokerName) => {
  const first = clientName?.split(' ')[0] || 'there'
  const phone = BROKER_PHONES[brokerName] || '1300 553 519'
  const brokerFirst = brokerName?.split(' ')[0] || 'Your broker'
  const sig = `${brokerFirst}, Benchmark Insurance Brokers\n📞 ${phone}`
  return text
    .replace(/{{first}}/g, first)
    .replace(/{{sig}}/g, sig)
}

function getSmsSegments(text) {
  const len = text.length
  if (len === 0) return { chars: 0, segments: 0 }
  if (len <= 160) return { chars: len, segments: 1 }
  return { chars: len, segments: Math.ceil(len / 153) }
}

export function SmsComposeModal({ client, user, onSend, onClose, sending, initialTemplate }) {
const [smsBody, setSmsBody] = useState('')
  const [selectedKey, setSelectedKey] = useState(null)
  const [alsoEmail, setAlsoEmail] = useState(false)
  const textareaRef = useRef(null)

  const firstName = client.company_name?.split(' ')[0] || 'there'
  const brokerFirst = user?.name?.split(' ')[0] || 'Your broker'
  const phone = BROKER_PHONES[user?.name] || '1300 553 519'
  const sig = `${brokerFirst}, Benchmark Insurance Brokers\n📞 ${phone}`
  const hasPhone = !!client.phone
  const hasEmail = !!(client.email && client.email.trim())

  // Auto-derive a sensible subject from the selected quick-start
  const SUBJECT_MAP = {
    quote_ready: 'Your insurance quote is ready',
    policy_confirmed: 'Your insurance cover is confirmed',
    renewal: 'Your insurance renewal is coming up',
    awaiting_info: 'A quick update on your insurance',
    claim_update: 'An update on your claim',
    missed_call: 'Trying to reach you — Benchmark Insurance',
    follow_up: 'Following up on your insurance',
    final_follow_up: 'Your Benchmark Insurance file',
  }
  const emailSubject = SUBJECT_MAP[selectedKey] || 'A message from Benchmark Insurance Brokers'

  useEffect(() => {
    const mappedKey = LEGACY_TEMPLATE_MAP[initialTemplate] || initialTemplate
    if (mappedKey && SMS_TEMPLATES[mappedKey]) {
      const filled = fillText(SMS_TEMPLATES[mappedKey].text, client.company_name, user?.name)
      setSmsBody(filled)
      setSelectedKey(mappedKey)
    }
    setTimeout(() => textareaRef.current?.focus(), 100)
  }, [])

  const applyQuickStart = (qs) => {
    const filled = fillText(qs.text, client.company_name, user?.name)
    setSmsBody(filled)
    setSelectedKey(qs.key)
    setTimeout(() => textareaRef.current?.focus(), 50)
  }

  const insertGreeting = () => {
    const greeting = `Hi ${firstName}, `
    if (smsBody.startsWith(`Hi ${firstName}`)) return
    setSmsBody(greeting + smsBody)
    setTimeout(() => {
      const ta = textareaRef.current
      if (ta) { ta.focus(); ta.setSelectionRange(greeting.length, greeting.length) }
    }, 50)
  }

  const appendSig = () => {
    const sigBlock = `\n\n${sig}`
    if (smsBody.includes(brokerFirst + ', Benchmark')) return
    setSmsBody(prev => prev + sigBlock)
    setTimeout(() => textareaRef.current?.focus(), 50)
  }

  const clearAll = () => { setSmsBody(''); setSelectedKey(null); setTimeout(() => textareaRef.current?.focus(), 50) }

  const { chars, segments } = getSmsSegments(smsBody)

  return (
    <div className="overlay" onClick={e => e.target === e.currentTarget && onClose()} onMouseDown={e => { if (e.target === e.currentTarget) e.preventDefault() }}>
      <div className="modal" style={{ maxWidth: 540, width: '94vw', maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column' }} onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div style={{ background: 'var(--forest)', padding: '18px 22px', display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <div style={{ width: 38, height: 38, borderRadius: 10, background: 'rgba(255,255,255,0.12)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18 }}>💬</div>
            <div>
              <div style={{ fontSize: 15, fontWeight: 700, color: '#fff' }}>Send SMS</div>
              <div style={{ fontSize: 12, color: 'rgba(255,255,255,0.6)', marginTop: 1 }}>
                {client.company_name}{hasPhone ? ` · ${client.phone}` : ' · ⚠ No phone'}
              </div>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'rgba(255,255,255,0.1)', border: 'none', color: 'rgba(255,255,255,0.7)', width: 30, height: 30, borderRadius: 8, cursor: 'pointer', fontSize: 15, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>✕</button>
        </div>

        {!hasPhone && (
          <div style={{ padding: '10px 22px', background: 'rgba(220,38,38,0.06)', borderBottom: '1px solid rgba(220,38,38,0.12)', fontSize: 12, fontWeight: 600, color: 'var(--danger)', flexShrink: 0 }}>
            ⚠ No phone number on file — add one to the client record before sending.
          </div>
        )}

        {/* ── Body ── */}
        <div style={{ flex: 1, overflowY: 'auto', padding: '18px 22px 4px' }}>

          {/* Quick starts */}
          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', marginBottom: 10 }}>Quick Start</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7 }}>
              {QUICK_STARTS.map(qs => (
                <button key={qs.key} onClick={() => applyQuickStart(qs)} style={{ padding: '7px 13px', borderRadius: 20, border: `1.5px solid ${selectedKey === qs.key ? 'var(--forest)' : 'var(--border)'}`, background: selectedKey === qs.key ? 'var(--forest)' : 'var(--surface2)', color: selectedKey === qs.key ? '#fff' : 'var(--text)', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', transition: 'all .15s', whiteSpace: 'nowrap' }}>
                  {qs.label}
                </button>
              ))}
            </div>
          </div>

          {/* Insert chips */}
          <div style={{ display: 'flex', gap: 7, marginBottom: 10, flexWrap: 'wrap' }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--muted)', textTransform: 'uppercase', letterSpacing: '.6px', alignSelf: 'center', marginRight: 4 }}>Insert:</div>
            <button onClick={insertGreeting} style={{ padding: '5px 12px', borderRadius: 16, border: '1.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>
              👋 Hi {firstName},
            </button>
            <button onClick={appendSig} style={{ padding: '5px 12px', borderRadius: 16, border: '1.5px solid var(--border)', background: 'var(--surface)', color: 'var(--text)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}>
              ✍️ Signature
            </button>
            {smsBody && (
              <button onClick={clearAll} style={{ padding: '5px 12px', borderRadius: 16, border: '1.5px solid rgba(220,53,69,0.2)', background: 'rgba(220,53,69,0.05)', color: 'var(--danger)', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)', marginLeft: 'auto' }}>
                Clear
              </button>
            )}
          </div>

          {/* Textarea */}
          <div style={{ border: `1.5px solid var(--border)`, borderRadius: 10, overflow: 'hidden', background: 'var(--surface)', transition: 'border-color .15s' }}>
            <textarea
              ref={textareaRef}
              value={smsBody}
              onChange={e => { setSmsBody(e.target.value); setSelectedKey(null) }}
              placeholder={`Hi ${firstName}, `}
              rows={7}
              style={{ width: '100%', border: 'none', outline: 'none', resize: 'none', padding: '14px 16px', fontSize: 13, lineHeight: 1.75, fontFamily: 'var(--font)', color: 'var(--text)', background: 'transparent', boxSizing: 'border-box' }}
              onFocus={e => e.currentTarget.parentElement.style.borderColor = 'var(--forest)'}
              onBlur={e => e.currentTarget.parentElement.style.borderColor = 'var(--border)'}
            />
            <div style={{ padding: '8px 16px', borderTop: '1px solid var(--border)', background: 'var(--surface2)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <div style={{ fontSize: 11, color: 'var(--muted)' }}>
                {chars > 0 ? (
                  <>
                    <span style={{ fontWeight: 600 }}>{chars}</span> chars · <span style={{ fontWeight: 700, color: segments > 2 ? 'var(--danger)' : segments > 1 ? '#b45309' : 'var(--forest)' }}>{segments} SMS{segments !== 1 ? 's' : ''}</span>
                  </>
                ) : <span>No message yet</span>}
              </div>
              {segments > 2 && <div style={{ fontSize: 10, color: 'var(--danger)', fontWeight: 600 }}>Consider shortening</div>}
            </div>
          </div>
        </div>

{/* ── Also email toggle ── */}
        <div style={{ padding: '10px 22px 0', flexShrink: 0 }}>
          <label
            title={hasEmail ? '' : 'No email on file for this client'}
            onClick={() => hasEmail && setAlsoEmail(v => !v)}
            style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: hasEmail ? 'pointer' : 'not-allowed', opacity: hasEmail ? 1 : 0.45, padding: '8px 12px', borderRadius: 10, border: `1.5px solid ${alsoEmail ? 'var(--forest)' : 'var(--border)'}`, background: alsoEmail ? 'var(--forest-subtle)' : 'var(--surface2)', transition: 'all .15s', userSelect: 'none' }}
          >
            <div style={{ width: 38, height: 22, borderRadius: 12, background: alsoEmail ? 'var(--forest)' : 'var(--border)', position: 'relative', flexShrink: 0, transition: 'background .15s' }}>
              <div style={{ width: 18, height: 18, borderRadius: '50%', background: '#fff', position: 'absolute', top: 2, left: alsoEmail ? 18 : 2, transition: 'left .15s', boxShadow: '0 1px 3px rgba(0,0,0,0.2)' }} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>📧 Also send as a branded email</div>
              <div style={{ fontSize: 11, color: 'var(--muted)', marginTop: 1 }}>
                {hasEmail ? <>To {client.email} · CC {brokerFirst}</> : 'No email on file for this client'}
              </div>
            </div>
          </label>
        </div>

        {/* ── Footer ── */}
        <div style={{ padding: '14px 22px', borderTop: '1.5px solid var(--border)', background: 'var(--surface)', display: 'flex', gap: 10, flexShrink: 0, marginTop: 12 }}>
          <button className="btn btn-secondary" onClick={onClose} style={{ flex: 1 }}>Cancel</button>
          <button className="btn btn-primary" onClick={() => onSend(smsBody, alsoEmail && hasEmail, emailSubject)} disabled={sending || !smsBody.trim() || !hasPhone} style={{ flex: 2, opacity: (sending || !smsBody.trim() || !hasPhone) ? 0.5 : 1 }}>
            {sending ? 'Sending...' : (alsoEmail && hasEmail ? '📱 Send SMS + 📧 Email' : '📱 Send SMS')}
          </button>
        </div>
      </div>
    </div>
  )
}
